import { Badge } from '@ovComponents/2-component/badge/badge';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { useDataTable } from '@ovComponents/2-component/dataTable';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import type { Theme } from 'hephaestus/core';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from 'hephaestus/web';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from 'hephaestus/webIcons';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { useModelColumns } from '../hooks/useModelColumns';
import {
  ALIGNED_TABLE_SX,
  BOLD_SX,
  LABEL_SX,
  MICRO_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  TABULAR_SX,
  TRUNCATE_SX,
} from '../styles';
import type { ModelAggregation, PortfolioReport } from '../types';
import { calculateDrift, getPortfolioName, getStatusCode } from '../utils';
import {
  PORTFOLIO_TRADING_PHANTOM_COLUMN_IDS,
  PortfolioTradingTableHead,
} from './portfolioTradingTableHead';

const driftColorPct = (theme: Theme, pct: number) =>
  pct > 10 ? theme.color.negative : pct > 5 ? theme.color.warning : undefined;

type ModelPortfolioViewProps = {
  models: ModelAggregation[];
  searchTerm: string;
  onRebalanceSelected?: (portfolios: PortfolioReport[]) => void;
  onSelectPortfolio?: (portfolioId: string) => void;
};

const STATUS_LABELS: Record<string, string> = {
  LIQ: 'Liquidating',
  XFER: 'Transfer',
  DRIFT: 'Drifted',
  PEND: 'Pending',
  OK: 'On Track',
};

const ModelRow = ({
  model,
  isSelected,
  onToggle,
  expanded,
  onToggleExpand,
  theme,
  onSelectPortfolio,
  tableColSpan,
}: {
  model: ModelAggregation;
  isSelected: boolean;
  onToggle: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  theme: Theme;
  onSelectPortfolio?: (portfolioId: string) => void;
  tableColSpan: number;
}) => {
  const formatMoney = useFormatMoney();
  const [showEmpty, setShowEmpty] = useState(false);
  const [sortCol, setSortCol] = useState<
    'value' | 'cash' | 'drift' | 'status' | 'trades'
  >('value');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  };
  const cashPct =
    model.totalMarketValueCents > 0
      ? (model.totalCashCents / model.totalMarketValueCents) * 100
      : 0;
  const expandedStats = [
    { label: 'Portfolios', value: `${model.portfolioCount}` },
    { label: 'Total AUM', value: formatMoney(model.totalMarketValueCents) },
    { label: 'Total Cash', value: formatMoney(model.totalCashCents) },
    {
      label: 'Cash %',
      value: `${cashPct.toFixed(1)}%`,
      color:
        cashPct > 10
          ? theme.color.warning
          : cashPct > 5
            ? theme.color.onSurfaceVariant
            : undefined,
    },
    {
      label: 'Avg Drift',
      value: `${model.avgDriftPct.toFixed(1)}%`,
      color: driftColorPct(theme, model.avgDriftPct),
    },
    {
      label: 'Need Action',
      value: `${model.needsRebalanceCount}`,
      color: model.needsRebalanceCount > 0 ? theme.color.warning : undefined,
    },
  ];

  const headers: { label: string; sortKey?: typeof sortCol }[] = [
    { label: 'Portfolio' },
    { label: 'Mkt Value', sortKey: 'value' },
    { label: 'Cash', sortKey: 'cash' },
    { label: 'Drift', sortKey: 'drift' },
    { label: 'Status', sortKey: 'status' },
    { label: 'Trades', sortKey: 'trades' },
  ];

  return (
    <>
      <TableRow
        sx={{
          cursor: 'pointer',
          '&:hover': { bgcolor: theme.color.neutralHover },
          bgcolor: isSelected ? theme.color.primaryVariant : undefined,
        }}
        onClick={onToggleExpand}
      >
        <TableCell>
          <Box
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckboxField
              checked={isSelected}
              onChange={onToggle}
              customStyle={{ transform: 'scale(0.8)' }}
            />
          </Box>
        </TableCell>
        <TableCell>
          <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton
              size='xsmall'
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} fontSize='inherit' />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 16 }}
                  fontSize='inherit'
                />
              )}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell bold>{model.modelName}</TableCell>
        <TableCell center bold>
          {model.portfolioCount}
        </TableCell>
        <TableCell right number>
          {formatMoney(model.totalMarketValueCents)}
        </TableCell>
        <TableCell
          right
          number
          sx={{ color: driftColorPct(theme, model.avgDriftPct) }}
        >
          {model.avgDriftPct.toFixed(1)}%
        </TableCell>
        <TableCell center>
          {model.needsRebalanceCount > 0 ? (
            <Badge label={`${model.needsRebalanceCount}`} color='warning' />
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell center bold>
          {model.pendingTradesCount > 0 ? model.pendingTradesCount : '—'}
        </TableCell>
        <TableCell right>{model.currencies.join(', ')}</TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell
            colSpan={tableColSpan}
            sx={{ py: 0, borderBottom: 'none' }}
          >
            <Box
              sx={{
                bgcolor: theme.color.surface,
                borderTop: '2px solid',
                borderColor: theme.color.primary,
                borderBottom: '1px solid',
                borderBottomColor: theme.color.outlineVariant,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 0,
                  borderBottom: '1px solid',
                  borderColor: theme.color.outlineVariant,
                }}
              >
                {expandedStats.map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      flex: 1,
                      py: 0.75,
                      px: 1.5,
                      borderRight: '1px solid',
                      borderColor: theme.color.outlineVariant,
                      '&:last-child': { borderRight: 'none' },
                    }}
                  >
                    <Typography
                      variant='labelSmall'
                      component='div'
                      sx={{ ...SECTION_HEADER_SX }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      variant='bodySmall'
                      component='div'
                      sx={{
                        ...BOLD_SX,
                        color: stat.color || theme.color.onSurface,
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 100px 90px 70px 72px 60px',
                    gap: 0,
                    py: 0.5,
                    px: 1.5,
                    borderBottom: '1px solid',
                    borderColor: theme.color.outlineVariant,
                    bgcolor: theme.color.neutralHover,
                  }}
                >
                  {headers.map((h) => (
                    <Typography
                      key={h.label}
                      variant='labelSmall'
                      onClick={
                        h.sortKey ? () => handleSort(h.sortKey!) : undefined
                      }
                      sx={{
                        ...SECTION_HEADER_SX,
                        textAlign: h.label === 'Portfolio' ? 'left' : 'right',
                        cursor: h.sortKey ? 'pointer' : undefined,
                        userSelect: h.sortKey ? 'none' : undefined,
                        '&:hover': h.sortKey
                          ? { color: theme.color.onSurface }
                          : undefined,
                      }}
                    >
                      {h.label}
                      {h.sortKey && sortCol === h.sortKey && (
                        <Box component='span' sx={{ ...MICRO_SX, ml: 0.3 }}>
                          {sortDir === 'asc' ? '▲' : '▼'}
                        </Box>
                      )}
                    </Typography>
                  ))}
                </Box>

                {(() => {
                  const getSortVal = (p: PortfolioReport): number => {
                    if (sortCol === 'value') return p.marketValueCents || 0;
                    if (sortCol === 'cash') return p.cashCents || 0;
                    if (sortCol === 'drift') return calculateDrift(p) ?? 0;
                    if (sortCol === 'trades')
                      return p.pendingSubTradeRequests || 0;
                    if (sortCol === 'status') {
                      const s = getStatusCode(p);
                      const order: Record<string, number> = {
                        LIQ: 4,
                        DRIFT: 3,
                        XFER: 2,
                        PEND: 1,
                        OK: 0,
                      };
                      return order[s] ?? 0;
                    }
                    return p.marketValueCents || 0;
                  };
                  const mult = sortDir === 'asc' ? 1 : -1;
                  const allSorted = [...model.portfolios].sort(
                    (a, b) => mult * (getSortVal(a) - getSortVal(b)),
                  );
                  const withValue = allSorted.filter(
                    (p) =>
                      (p.marketValueCents || 0) > 0 || (p.cashCents || 0) > 0,
                  );
                  const zeroRows = allSorted.filter(
                    (p) =>
                      (p.marketValueCents || 0) === 0 &&
                      (p.cashCents || 0) === 0,
                  );
                  const display = showEmpty
                    ? [...withValue, ...zeroRows]
                    : withValue;
                  return (
                    <>
                      {display.map((p) => {
                        const drift = calculateDrift(p);
                        const status = getStatusCode(p);
                        const statusLabel = STATUS_LABELS[status] || status;
                        const isZero =
                          (p.marketValueCents || 0) === 0 &&
                          (p.cashCents || 0) === 0;
                        const driftAbs = drift !== null ? Math.abs(drift) : 0;
                        return (
                          <Box
                            key={p.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns:
                                '1fr 100px 90px 70px 72px 60px',
                              gap: 0,
                              py: 0.4,
                              px: 1.5,
                              borderBottom: '1px solid',
                              borderColor: theme.color.outlineVariant,
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { bgcolor: theme.color.neutralHover },
                              opacity: isZero ? 0.4 : 1,
                            }}
                          >
                            <Box
                              sx={{
                                overflow: 'hidden',
                              }}
                            >
                              <Typography
                                variant='bodySmall'
                                component='div'
                                sx={{
                                  fontWeight: 600,
                                  ...TRUNCATE_SX,
                                  ...(onSelectPortfolio && {
                                    cursor: 'pointer',
                                    '&:hover': {
                                      color: theme.color.primary,
                                      textDecoration: 'underline',
                                    },
                                  }),
                                }}
                                onClick={
                                  onSelectPortfolio
                                    ? () => onSelectPortfolio(p.id)
                                    : undefined
                                }
                              >
                                {getPortfolioName(p)}
                              </Typography>
                              <Typography
                                variant='labelSmall'
                                component='div'
                                sx={{ ...SECONDARY_TEXT_SX }}
                              >
                                {p.modelPortfolioType}
                              </Typography>
                            </Box>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {formatMoney(p.marketValueCents)}
                            </Typography>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                ...TABULAR_SX,
                                textAlign: 'right',
                                color: theme.color.onSurfaceVariant,
                              }}
                            >
                              {formatMoney(p.cashCents)}
                            </Typography>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                ...TABULAR_SX,
                                textAlign: 'right',
                                fontWeight: driftAbs > 3 ? 700 : 400,
                                color:
                                  drift !== null
                                    ? (driftColorPct(theme, driftAbs) ??
                                      theme.color.onSurface)
                                    : theme.color.onSurface,
                              }}
                            >
                              {drift !== null ? `${drift.toFixed(1)}%` : '—'}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Badge
                                label={statusLabel}
                                color={
                                  status === 'OK'
                                    ? 'positive'
                                    : status === 'PEND'
                                      ? 'neutral'
                                      : 'warning'
                                }
                              />
                            </Box>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                textAlign: 'right',
                                fontWeight:
                                  p.pendingSubTradeRequests > 0 ? 600 : 400,
                                color:
                                  p.pendingSubTradeRequests > 0
                                    ? theme.color.onSurface
                                    : theme.color.onDisabled,
                              }}
                            >
                              {p.pendingSubTradeRequests || '—'}
                            </Typography>
                          </Box>
                        );
                      })}
                      {zeroRows.length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 0.5,
                            borderTop: '1px solid',
                            borderColor: theme.color.outlineVariant,
                          }}
                        >
                          <Typography
                            variant='labelSmall'
                            onClick={() => setShowEmpty((v) => !v)}
                            sx={{
                              ...LABEL_SX,
                              cursor: 'pointer',
                              color: theme.color.primary,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {showEmpty
                              ? `Hide ${zeroRows.length} empty`
                              : `Show all (${zeroRows.length} with $0)`}
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ModelPortfolioView = ({
  models,
  searchTerm,
  onRebalanceSelected,
  onSelectPortfolio,
}: ModelPortfolioViewProps) => {
  const theme = useTheme();
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());

  const toggleSet = useCallback(
    (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [],
  );

  const handleToggleModel = useCallback(
    (modelId: string) => toggleSet(setSelectedModels, modelId),
    [toggleSet],
  );
  const handleToggleExpand = useCallback(
    (modelId: string) => toggleSet(setExpandedModels, modelId),
    [toggleSet],
  );

  const filteredModels = useMemo(() => {
    if (!searchTerm) return models;
    const lower = searchTerm.toLowerCase();
    return models.filter((m) => m.modelName.toLowerCase().includes(lower));
  }, [models, searchTerm]);

  const columns = useModelColumns();
  const { table } = useDataTable({
    data: filteredModels,
    columns,
    sortingMode: 'client',
  });
  const modelTableColSpan = table.getAllLeafColumns().length;

  const allSelected =
    filteredModels.length > 0 &&
    filteredModels.every((m) => selectedModels.has(m.modelId));
  const someSelected = filteredModels.some((m) =>
    selectedModels.has(m.modelId),
  );

  const handleSelectAll = useCallback(() => {
    setSelectedModels((prev) =>
      prev.size === filteredModels.length
        ? new Set()
        : new Set(filteredModels.map((m) => m.modelId)),
    );
  }, [filteredModels]);

  const handleClearSelection = useCallback(
    () => setSelectedModels(new Set()),
    [],
  );

  const selectedPortfolios = useMemo(() => {
    const portfolios: PortfolioReport[] = [];
    filteredModels.forEach((m) => {
      if (selectedModels.has(m.modelId)) portfolios.push(...m.portfolios);
    });
    return portfolios;
  }, [selectedModels, filteredModels]);

  const selectedPortfolioCount = selectedPortfolios.length;

  return (
    <Box>
      {selectedModels.size > 0 && (
        <Box
          sx={{
            mb: 1,
            px: 2,
            py: 1,
            bgcolor: theme.color.primaryVariant,
            border: '1px solid',
            borderColor: theme.color.primary,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='bodySmall' sx={{ ...SECONDARY_TEXT_SX }}>
            <strong style={{ color: 'inherit' }}>{selectedModels.size}</strong>{' '}
            model{selectedModels.size !== 1 ? 's' : ''} selected ·{' '}
            <strong>{selectedPortfolioCount}</strong> portfolio
            {selectedPortfolioCount !== 1 ? 's' : ''}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button variant='text' size='small' onClick={handleClearSelection}>
              Clear
            </Button>
            <Button
              variant='contained'
              size='small'
              onClick={() => onRebalanceSelected?.(selectedPortfolios)}
              disabled={selectedPortfolioCount === 0}
            >
              {`Rebalance ${selectedPortfolioCount} Portfolio${selectedPortfolioCount !== 1 ? 's' : ''}`}
            </Button>
          </Box>
        </Box>
      )}
      <Table sx={{ ...ALIGNED_TABLE_SX }}>
        <PortfolioTradingTableHead
          table={table}
          skipColumnIds={PORTFOLIO_TRADING_PHANTOM_COLUMN_IDS.model}
          rowSx={{ bgcolor: theme.color.background }}
          leadingCells={[
            {
              key: 'select',
              sx: { width: 40 },
              content: (
                <CheckboxField
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={handleSelectAll}
                  customStyle={{ transform: 'scale(0.8)' }}
                />
              ),
            },
            { key: 'expand', sx: { width: 40 } },
          ]}
        />

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <ModelRow
              key={row.original.modelId}
              model={row.original}
              isSelected={selectedModels.has(row.original.modelId)}
              onToggle={() => handleToggleModel(row.original.modelId)}
              expanded={expandedModels.has(row.original.modelId)}
              onToggleExpand={() => handleToggleExpand(row.original.modelId)}
              theme={theme}
              onSelectPortfolio={onSelectPortfolio}
              tableColSpan={modelTableColSpan}
            />
          ))}
        </TableBody>
      </Table>

      {filteredModels.length === 0 && (
        <Box
          sx={{
            py: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant='bodySmall' sx={{ ...SECONDARY_TEXT_SX }}>
            {searchTerm
              ? 'No models match your search'
              : 'No model portfolio data available'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ModelPortfolioView;
